import { camelToSnakeCase } from 'tio-common/utils/string-case-conversion';
import { buildReportQueryCore, type ReportQueryCoreOptions } from './report-search-body';
import { searchRequestFor } from './search-requests';
import type { Store as StoreService } from '@warp-drive/core';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import type { FieldOption } from './opensearch-field-mapper';

const MAX_DISTINCT_VALUES = 500;
// Use camelCase agg name — the Rails API camelizes response keys
const AGG_NAME = 'distinctVals';

/** Returns true for fields whose distinct values can be fetched (keyword/text types). */
export function shouldFetchDistinctValues(field: FieldOption): boolean {
  // The dropdown's terms aggregation is a GROUP BY over the filtered set —
  // when the author disabled grouping on a column (SQL datasets, plan
  // control), the dropdown scan must not run either; the filter UI falls
  // back to free-text input.
  if (field.isGroupable === false) return false;
  return field.type === 'keyword' || field.type === 'text';
}

/**
 * Query context for a distinct-values fetch — the report's query-core options
 * minus what rides positionally (modelType, companyId). The facet query is the
 * report's result query (same shared assembly — buildReportQueryCore) minus
 * the fetched field's own facet selection, so dropdown options always reflect
 * the rows the viewer can actually reach.
 *
 * `buildFilterValues` are hard constraints — applied INCLUDING to the fetched
 * field's own list (a report built on a subset must never offer values outside
 * it). `userFilterValues` are facet selections — applied to every list except
 * the fetched field's own (standard faceting keeps a multi-select's remaining
 * values reachable). Callers where the fetched field's filter is itself
 * editable (the report builder) pass everything as `userFilterValues`.
 */
export interface DistinctValuesFilterContext extends Omit<
  ReportQueryCoreOptions,
  'modelType' | 'companyId' | 'fields' | 'forceSqlCompanyTerm'
> {
  /** Dataset fields for OpenSearch path resolution + queryHints dispatch. */
  fields?: FieldOption[];
}

/**
 * Fetches distinct values for a field via OpenSearch terms aggregation.
 * Returns sorted string array if ≤ 500 distinct values, otherwise null.
 */
export async function fetchDistinctValues(
  store: StoreService,
  fieldKey: string,
  companyId?: string,
  filterContext?: DistinctValuesFilterContext,
  modelType?: string,
  aggFieldPath?: string
): Promise<string[] | null> {
  const ctx = filterContext ?? {};
  const requestModelType = modelType || 'TasProgramInstance';

  // Facet self-exclusion applies ONLY to user filter values — build filters
  // on the same field remain as hard constraints.
  const facetValues = Object.fromEntries(
    Object.entries(ctx.userFilterValues ?? {}).filter(
      ([key]) => key !== fieldKey && !key.startsWith(`${fieldKey}__`)
    )
  );

  // Same query assembly as the result set (company scoping incl. the SQL
  // dataset param rule, layout-aware build filters, relative-date resolution,
  // HAVING-consumed keys excluded).
  const { builder } = buildReportQueryCore({
    ...ctx,
    modelType: requestModelType,
    fields: ctx.fields ?? [],
    userFilterValues: facetValues,
    companyId,
  });
  builder.setPagination(0, 0);

  // Field keys are camelCase (e.g. tasProgramTemplateCodeKeyword) but the
  // OpenSearch index uses snake_case (e.g. tas_program_template_code_keyword)
  const snakeField = aggFieldPath ?? camelToSnakeCase(fieldKey);
  builder.addTermsAggregation(AGG_NAME, snakeField, MAX_DISTINCT_VALUES + 1);

  // Execute via the shared search builder; unwrap `.content`.
  const { content: response } = await store.request<OpenSearchResponse>(
    searchRequestFor(
      {
        'model-type': requestModelType,
        searchBody: builder.build(),
      },
      companyId
    )
  );

  // The Rails API camelizes response keys, so aggregations are at
  // response.aggregations. The terms aggregation is keyed by AGG_NAME, which is
  // outside the typed OpenSearchResponse.aggregations shape — narrow via a loose
  // cast (the injected adapter's untyped return previously allowed this).
  const aggs = response?.aggregations as unknown as
    | Record<string, { buckets?: Array<{ key: string; docCount?: number; doc_count?: number }> }>
    | undefined;

  const buckets = aggs?.[AGG_NAME]?.buckets || [];

  if (buckets.length > MAX_DISTINCT_VALUES) {
    return null;
  }

  return buckets
    .map((b) => String(b.key))
    .filter((v) => v !== '' && v !== 'null' && v !== 'undefined')
    .sort((a, b) => a.localeCompare(b));
}
